import { FetchResult } from 'src/types/TApiQueries';
import { TSubscription } from 'src/types/TSubscription';
import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';

export async function getSubscription(accessToken: string): Promise<FetchResult<TSubscription>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/subscription`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// GET /api/v1/account/write-status — readable by EVERY member, unlike
// GET /subscription (owner/admins only). Minimal on purpose: no plan, price or
// usage. `dormant` accounts report `write_blocked: true` but wake on their next
// write, so callers must keep write actions enabled for them — use
// `useWritesPaused`, which encodes that, rather than reading this directly.
export type TWriteStatus = {
  write_blocked: boolean;
  reason: string | null;
  dormant: boolean;
  /** Low-/out-of-credit banner state. Not a write block: uploads still
   *  succeed. Always null for the User role. */
  credit_alert?: TCreditAlert | null;
};

// `credit_alert` on GET /account/write-status and GET /subscription.
// `out`: something was refused for lack of credits (a video run, an AI
// action) — `blocked_count` of them. `low`: the balance crossed below the
// low-credit level (20% of the plan's monthly credits, or 100).
export type TCreditAlert = {
  kind: 'low' | 'out';
  since: string | null;
  balance: number;
  threshold: number;
  blocked_count: number;
};

export async function getWriteStatus(accessToken: string): Promise<FetchResult<TWriteStatus>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/account/write-status`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// Payload returned by the backend checkout-session endpoint. The frontend
// mounts `client_secret` in an in-app EmbeddedCheckout modal.
export type TCheckoutSession = {
  client_secret: string;
  session_id: string;
  plan_code: string;
};

// POST /api/v1/billing/checkout-session — creates an embedded Stripe Checkout
// Session (mode=subscription) for the target plan and returns its client_secret.
// Owner-only; 503 when billing is unconfigured.
export async function createCheckoutSession(
  planCode: string,
  accessToken: string,
): Promise<FetchResult<TCheckoutSession>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/checkout-session`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ plan_code: planCode }),
  });
}

// POST /api/v1/billing/cancel — cancel at period end with a reason from the
// fixed list. Returns {status:'cancelled', effective_at}. Owner-only.
export type TCancelResult = {
  status: string;
  effective_at: string | null;
};

export async function cancelSubscription(
  reasonCode: string,
  reasonText: string | null,
  accessToken: string,
): Promise<FetchResult<TCancelResult>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/cancel`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ reason_code: reasonCode, reason_text: reasonText }),
  });
}

// POST /api/v1/billing/reactivate — reverse a scheduled cancellation while
// still inside the paid window. Owner-only.
export async function reactivateSubscription(
  accessToken: string,
): Promise<FetchResult<{ status: string }>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/reactivate`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({}),
  });
}

// Change-plan preview + apply (Phase 4). Upgrades apply immediately and are
// charged the prorated difference at once; downgrades apply at the next
// renewal (no charge). Preview reports the timing, and for an upgrade the
// amount charged now, so the UI can set expectations.
export type TPlanChangePreview = {
  current_plan: string;
  target_plan: string;
  target_plan_name: string | null;
  is_same: boolean;
  is_upgrade: boolean;
  is_downgrade: boolean;
  apply_timing: 'immediate' | 'renewal' | 'none';
  prorated: boolean;
  effective_at: string | null;
  /** Upgrade only: what is charged now, priced at `proration_date`. Null
   *  when it can't be priced (no Stripe binding, Stripe unavailable). */
  amount_due_now_usd?: number | null;
  currency?: string | null;
  /** Unix seconds the amount was priced at; send it back with the change. */
  proration_date?: number | null;
};

export async function previewPlanChange(
  planCode: string,
  accessToken: string,
): Promise<FetchResult<TPlanChangePreview>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/change-plan/preview`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ plan_code: planCode }),
  });
}

export type TPlanChangeResult = {
  applied: 'immediate' | 'renewal' | 'none';
  pending_plan: string | null;
  effective_at: string | null;
};

// A 402 means the upgrade's immediate charge didn't go through (declined, or
// the bank wants authentication); nothing changed.
export async function changePlan(
  planCode: string,
  accessToken: string,
  prorationDate?: number | null,
): Promise<FetchResult<TPlanChangeResult>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/change-plan`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(
      prorationDate ? { plan_code: planCode, proration_date: prorationDate } : { plan_code: planCode },
    ),
  });
}

// Self-serve plan catalog (plan-structure v2 Phase 7). One read-through source
// for the tier picker, the PAYG rate card and the plan comparison — so a
// repricing in `subscription_plans` reaches the UI with no frontend release.
export type TPlanQuota = {
  included: number;          // 0 == unlimited
  unit: string;              // unit of `included` (NB: cdn_bytes is in BYTES)
  overage_rate_usd: number | null;   // null when the dim isn't billed here
  rate_unit: string | null;  // unit the rate is charged in (cdn = per GB)
  // Derived viewer rule (migration 53), on `viewers` only: the account's
  // real cap is per_seat × contributor seats, which this account-agnostic
  // catalog can't resolve — render "N per contributor seat". Present only
  // on plans with the rule (PAYG).
  per_seat?: number;
  // Addon allowances (`print_exports`): AI credits charged per unit past the
  // allowance — print export is priced as one background removal. Absent on
  // the core meters, which carry a USD rate instead.
  overage_ai_credits?: number | null;
};

export type TCatalogPlan = {
  code: string;
  name: string;
  description: string;
  price_usd: number;
  // Monthly floor on a cycle with billable activity — PAYG's "from $5/mo,
  // fully applied to your usage". null on plans without one, so a card branches
  // on presence rather than on the plan code. Published by the catalog for the
  // same reason the prices are: a change must reach the UI without a release.
  monthly_minimum_usd?: number | null;
  is_trial: boolean;
  /** credits | minutes (conf/22); absent on an older backend = minutes. */
  transcode_billing?: 'credits' | 'minutes';
  quotas: Record<string, TPlanQuota>;
  addons: string[];
  // Schema add-on (migration 54): present only on plans that SELL extra
  // asset schemas (PAYG — first included, +$5/mo each). Branch on presence,
  // never on the plan code.
  schema_addon?: {
    max_extra: number;   // purchasable extras (PAYG: 6)
    max_total: number;   // included + max_extra (PAYG: 7)
  };
};

export type TPlanCatalog = {
  seat_price_usd: number;
  // Price of one extra asset schema/month — absent on backends predating
  // migration 54.
  schema_price_usd?: number;
  plans: TCatalogPlan[];
};

export async function getPlans(accessToken: string): Promise<FetchResult<TPlanCatalog>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/plans`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// Owner-set ceiling on metered charges ("pay as you go, no surprise bills").
//
// It caps the BILL, not the service: usage past the cap is still served and
// still metered, it simply isn't charged at cycle close. `minimum_usd` is the
// floor the cap may not go below (0 when the plan has none) — the UI uses it to
// bound its own input instead of discovering the limit through a 400.
export type TSpendCap = {
  spend_cap_usd: number | null;
  minimum_usd: number;
  currency: string;
};

export async function getSpendCap(
  accessToken: string,
): Promise<FetchResult<TSpendCap>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/spend-cap`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// `null` clears the cap. Owner-only, self-serve plans only; 400 when the value
// is negative or below the plan's monthly minimum.
export async function setSpendCap(
  spendCapUsd: number | null,
  accessToken: string,
): Promise<FetchResult<TSpendCap>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/spend-cap`, {
    method: 'PUT',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ spend_cap_usd: spendCapUsd }),
  });
}

// Extra contributor seats ($9/mo each — plan-structure v2 Phase 6/7).
// `count` is the ABSOLUTE number of extra seats, mirroring the quantity on the
// Stripe subscription item; the server updates Stripe FIRST and only mirrors
// locally if that succeeds. 0 removes the seat item entirely.
export type TSeatSummary = {
  plan_code: string;
  contributors: number;
  viewers: number;
  total: number;
  contributors_included: number;
  extra_seats: number;
  contributor_limit: number;
  viewer_limit: number;
};

export async function setExtraSeats(
  count: number,
  accessToken: string,
): Promise<FetchResult<TSeatSummary>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/seats`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ count }),
  });
}

// Extra asset schemas ($5/mo each, PAYG only — migration 54). Same contract
// as seats: `count` is ABSOLUTE, mirrors the Stripe item quantity, Stripe is
// written first, 0 removes the item. The plan's `included` (1 on PAYG) is
// never billed; `schema_limit` = included + extras; `max_extra` bounds the
// purchasable count (0 = the plan doesn't sell the add-on).
export type TSchemaAddonSummary = {
  plan_code: string;
  included: number;
  extra_schemas: number;
  schema_limit: number;
  max_extra: number;
  active_schemas: number;
};

export async function getSchemaAddon(
  accessToken: string,
): Promise<FetchResult<TSchemaAddonSummary>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/schemas`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// Live-count entitlement caps for the caller's subscription — readable by
// ANY member (unlike GET /api/v1/subscription), because these numbers gate
// actions any member can attempt. The pages use them to disable create
// buttons AT the cap instead of letting the click bounce off the 403.
// quota 0 == unlimited.
export type TPlanLimit = { used: number; quota: number };
export type TPlanLimits = {
  collections: TPlanLimit;
  shares: TPlanLimit;
  asset_schemas: TPlanLimit & {
    included: number;
    extra: number;
    max_extra: number;
  };
};

export async function getPlanLimits(
  accessToken: string,
): Promise<FetchResult<TPlanLimits>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/limits`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function setExtraSchemas(
  count: number,
  accessToken: string,
): Promise<FetchResult<TSchemaAddonSummary>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/schemas`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ count }),
  });
}

// AI-credit top-up packs (Phase F). GET the catalog; POST a session to get a
// Stripe Checkout URL (mode=payment) for a chosen SKU.
export type TTopupPack = {
  sku: string;
  name: string;
  credits: number;
  price_usd: number;
  validity_days: number | null;
};

export async function getTopupPacks(
  accessToken: string,
): Promise<FetchResult<{ packs: TTopupPack[] }>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/topup-packs`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// Customer-facing AI credit rate card (GET /api/v1/billing/ai-rates).
// `flat` = credits per operation, keyed by `ai_credit_rates.model`;
// `token` = token-billed rows (chat/agent), credits per token / per call.
export type TAiTokenRates = {
  input_rate: number;
  output_rate: number;
  tool_call_rate: number;
};

export type TAiRates = {
  flat: Record<string, number>;
  token: Record<string, TAiTokenRates>;
};

export async function getAiRates(
  accessToken: string,
): Promise<FetchResult<TAiRates>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/ai-rates`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export type TTopupSession = {
  client_secret: string;
  session_id: string;
  sku: string;
};

export async function createTopupSession(
  sku: string,
  accessToken: string,
): Promise<FetchResult<TTopupSession>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/topup-session`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ sku }),
  });
}

// In-app payment method + invoices via the Stripe Customer Portal. The session
// returns a hosted portal URL to redirect to; `return_url` is where the portal
// sends the browser back.
export type TPortalSession = {
  url: string;
};

export async function createPortalSession(
  returnUrl: string,
  accessToken: string,
): Promise<FetchResult<TPortalSession>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/payment-session`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ return_url: returnUrl }),
  });
}

// Invoice history (Phase 5).
export type TInvoice = {
  id: string | null;
  date: string | null;
  amount: string | null;
  currency: string | null;
  invoice_url: string;
};

// ONE page of recent invoices, not an archive. `has_more` is Stripe's own flag,
// passed straight through: when true there are older invoices the in-app list
// will never show, and the UI hands off to the Customer Portal (which
// paginates). Without it a truncated list silently reads as complete.
export type TInvoiceList = {
  invoices: TInvoice[];
  has_more: boolean;
};

export async function getInvoices(
  accessToken: string,
): Promise<FetchResult<TInvoiceList>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/billing/invoices`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// Feature trials (plan-structure v2 Phase 9). A plan gate's 403 carries
// `trial_available` + `addon_code`; POSTing here starts the one-and-only
// 14-day trial of that feature for the account. The capability itself is
// delivered server-side by the `is_addon_enabled` seam, so a successful start
// means the very next request to the gated endpoint succeeds.
export type TFeatureTrial = {
  feature_code: string;
  status: string;
  active: boolean;
  started_at: string | null;
  expires_at: string | null;
  days_remaining: number | null;
};

export type TFeatureTrialsResponse = {
  trials: TFeatureTrial[];
  available_features: string[];
  trial_days: number;
  trials_enabled: boolean;
};

export async function getFeatureTrials(
  accessToken: string,
): Promise<FetchResult<TFeatureTrialsResponse>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/features/trials`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function startFeatureTrial(
  featureCode: string,
  accessToken: string,
): Promise<FetchResult<{ status: string; trial: TFeatureTrial }>> {
  return performFetchWithLogging(
    `${config.hostUrl}/api/v1/features/${encodeURIComponent(featureCode)}/trial`,
    {
      method: 'POST',
      headers: getStandardHeaders(accessToken),
      credentials: 'include',
    },
  );
}
